import {
  observable, action, computed,
} from 'mobx';
import {
  OFFER_REMOVED,
  PRODUCT,
  SEVERITY_ABORT,
  GIFT_CARD,
  RESTRICTION_ERRORS,
} from '../../../constants/Errors';
import Notification, { INotificationOptions } from './Notification';
import { DisregardedNotifications, NOTIFICATION_GROUP, NOTIFICATION_TYPE } from '../../../constants/Notifications';
export const messageTimeoutDefaultMs = 5000;

class NotificationsCollection {
  @observable notifications: Notification[] = [];

  @observable tempNotifications: Notification[] = [];

  @action
  addNotification = (notification: INotificationOptions): void => {
    if (DisregardedNotifications.includes(notification.key)) {
      return;
    }

    const notificationMessage = Notification.fromJSON(notification);

    this.notifications.push(notificationMessage);

    if (notificationMessage.timeout) {
      setTimeout(() => {
        const index = this.notifications.findIndex(item => item.uuid === notificationMessage.uuid);
        this.notifications.splice(index, index >= 0 ? 1 : 0);
      }, notificationMessage.timeout);  
    }
  };

  @action
  addNotifications(notifications: Notification[] | []): void {
    this.notifications = [...this.notifications, ...notifications];
  }

  @action
  clearNotifications = (notificationGroup?: NOTIFICATION_GROUP, notificationType?: NOTIFICATION_TYPE): void => {
    this.notifications = this.notifications.filter(notification =>
      (notificationGroup ? notification.group !== notificationGroup : false) ||
      (notificationType ? notification.type !== notificationType : false) ||
      // notifications with timeout shouldn't be cleared
      !!notification.timeout);
  };

  @action
  clearNotificationByKey = (key: string): void => {
    this.notifications = this.notifications.filter(notification => notification.key !== key);
  };

  @action
  temporarilyStoreNotifications = (): void => {
    this.tempNotifications.push(...this.notifications);
    this.notifications = [];
  };

  @action
  restoreNotifications = (): void => {
    this.notifications.push(...this.tempNotifications);
    this.tempNotifications = [];
  };

  public hasNotification(key: string, group?: NOTIFICATION_GROUP): boolean {
    return (this.notifications || []).some(
      item => key === item.key &&
      (group ? item.group === group : true),
    );
  }

  public getNotifications(keys: string[], group?: NOTIFICATION_GROUP): Notification[] {
    return (this.notifications || []).filter(
      item => keys.includes(item.key) &&
      (group ? item.group === group : true),
    );
  }

  public hasNotificationsByGroup(group?: NOTIFICATION_GROUP, type?: NOTIFICATION_TYPE): boolean {
    return (this.notifications || []).some(notification =>
      (group ? notification.group === group : true) &&
      (type ? notification.type === type : true),
    );
  }

  @computed
  get hasNotifications(): boolean {
    return !!this.notifications.length;
  }

  @computed
  get hasErrors(): boolean {
    return !!this.notifications.some(item => item.type === NOTIFICATION_TYPE.DANGER);
  }


  @computed
  get offerRemoved(): string {
    return this.hasNotification(OFFER_REMOVED)
      && this.notifications.find(e => e.key === OFFER_REMOVED)?.offerRemoved;
  }

  @computed
  get shippingRestrictedProducts(): string {
    return this.hasNotification(RESTRICTION_ERRORS.SHIPPING_RESTRICTIONS_STATES)
      && this.notifications
        .find(e => e.key === RESTRICTION_ERRORS.SHIPPING_RESTRICTIONS_STATES).data.data.products as string;
  }

  @computed
  get hasRemovedProductsError(): boolean {
    return this.notifications.some(e => e.key.startsWith(PRODUCT.REMOVED_ITEM_SOLD_OUT)
    || e.key.startsWith(PRODUCT.REMOVED_ITEM_INACTIVE));
  }

  @computed
  get removedProducts(): string[] {
    return this.hasRemovedProductsError
      && this.notifications.filter(
        e => e.key.startsWith(PRODUCT.REMOVED_ITEM_SOLD_OUT)
        || e.key.startsWith(PRODUCT.REMOVED_ITEM_INACTIVE),
      ).map(item => item.productRgnName);
  }

  @computed
  get isAborted(): boolean {
    return this.notifications.some(item => item.severity === SEVERITY_ABORT);
  }

  @computed
  get isGiftCardBalanceZero(): boolean {
    return this.notifications.some(e => e.key === GIFT_CARD.ZERO_BALANCE);
  }
}

export default NotificationsCollection;
